import { useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { useParams } from 'react-router-dom';

import {
  Divider,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  MoreVertIcon,
} from '@occc/ui-design-system';
import { Retailer_Network_User_Role, RetailerNetworkUserFragment } from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import {
  UpdateRetailerNetworkUser,
  RemoveUserFromRetailerNetwork,
} from '@occc-app/App/PolicyBasedAccessControl/userActions';
import { formatPhoneNumberNationalForUS } from '@occc/utils-phone-number';

import { mapRetailerNetworkUserRoleToText } from '../../../retailer-connect.utils';
import { useRetailerNetworkUserSelector } from '../../../retailer-network-context/use-retailer-network-user.selector';

type MenuItem = 'userDetails' | 'removeUserFromLocationGroup' | 'removeUserFromRetailerNetwork';

type RetailerNetworkLocationGroupUsersTableRowProps = {
  item: RetailerNetworkUserFragment;
  isPhoneNumberVisible?: boolean;
  handleRedirectToUserDetailsPage: (userUuid: string) => void;
  handleRemoveUserFromLocationGroup: (userUuid: string) => void;
  handleRemoveUserFromRetailerNetwork: (openCommerceUserUuid: string) => void;
};

export const RetailerNetworkLocationGroupUsersTableRow = ({
  item,
  isPhoneNumberVisible = false,
  handleRedirectToUserDetailsPage,
  handleRemoveUserFromLocationGroup,
  handleRemoveUserFromRetailerNetwork,
}: RetailerNetworkLocationGroupUsersTableRowProps) => {
  const { networkId } = useParams<{ networkId: string }>();
  const { isRetailerNetworkSiteAdmin } = useRetailerNetworkUserSelector();

  const [actionsForUuid, setActionsForUuid] = useState<string | null>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { name, emailAddress, phoneNumber, role, uuid, openCommerceUser } = item;
  // Later we will add a separate policy which includes role in conditions
  // but for now we have to do that on FE side.
  const isTargetUserRetailerNetworkSiteAdmin = role === Retailer_Network_User_Role.SiteAdmin;

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    setActionsForUuid(null);
  };

  const getHandlerForAdditionalActions =
    (uuid: string) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(uuid);
    };

  const onMenuNavigationClick = (itemName: MenuItem) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (itemName === 'userDetails') {
      handleRedirectToUserDetailsPage(uuid);
    }

    if (itemName === 'removeUserFromLocationGroup') {
      handleRemoveUserFromLocationGroup(uuid);
    }

    if (itemName === 'removeUserFromRetailerNetwork' && openCommerceUser) {
      handleRemoveUserFromRetailerNetwork(openCommerceUser.uuid);
    }

    handleClose(event);
  };

  const onRowClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleRedirectToUserDetailsPage(uuid);
  };

  return (
    <TableRow key={uuid} onClick={onRowClick}>
      <TableCell>{name}</TableCell>
      <TableCell>{emailAddress}</TableCell>
      {isPhoneNumberVisible && (
        <TableCell>{formatPhoneNumberNationalForUS(phoneNumber || '')}</TableCell>
      )}
      <TableCell>
        <Typography variant='body2'>{mapRetailerNetworkUserRoleToText[role]}</Typography>
      </TableCell>
      <TableCell align='right'>
        <IconButton onClick={getHandlerForAdditionalActions(uuid)} sx={{ p: 0 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu open={uuid === actionsForUuid} onClose={handleClose} anchorEl={anchorEl}>
          <MenuItem onClick={onMenuNavigationClick('userDetails')}>View Details</MenuItem>

          <Can I={UpdateRetailerNetworkUser({ uuid: networkId || '' })}>
            {isRetailerNetworkSiteAdmin && isTargetUserRetailerNetworkSiteAdmin
              ? null
              : [
                  <Divider key='divider' />,
                  <MenuItem
                    key='remove-user-from-location-group-item'
                    onClick={onMenuNavigationClick('removeUserFromLocationGroup')}
                  >
                    Remove User from Location Group
                  </MenuItem>,
                ]}
          </Can>
          <Can I={RemoveUserFromRetailerNetwork({ uuid: networkId || '' })}>
            <Divider key='divider' />
            <MenuItem
              key='remove-user-from-retailer-network-item'
              onClick={onMenuNavigationClick('removeUserFromRetailerNetwork')}
            >
              Remove User from Network
            </MenuItem>
          </Can>
        </Menu>
      </TableCell>
    </TableRow>
  );
};
